import { useEffect, useState } from "react";
import type { ApiKeyField, IntegrationTool } from "../types/integration";

// IP-171/IP-205: fetch the public catalog detail for an integration — the
// "Available tools" list AND the API-key connect schema (`apiKeyFields`). Both
// are carried only on the single-slug response (list responses omit them to
// stay lean). Tenant-agnostic — the same for everyone, so this is independent
// of the per-tenant useIntegrations() fetch.

export type ToolsStatus = "loading" | "ready" | "error";

export type UseIntegrationToolsResult = {
  status: ToolsStatus;
  tools: ReadonlyArray<IntegrationTool>;
  // Required credential fields for an API-key connect. Empty unless this is a
  // connectable API_KEY integration; drives the dynamic connect form.
  apiKeyFields: ReadonlyArray<ApiKeyField>;
  // IP-217: the third-party app's homepage (Composio meta.app_url), the
  // universal fallback "where do I get my API key" link. Null when upstream
  // carries none. Like apiKeyFields, only present on the single-slug response.
  appUrl: string | null;
  error: string | null;
};

type DetailResponse = {
  tools?: ReadonlyArray<IntegrationTool> | null;
  apiKeyFields?: ReadonlyArray<ApiKeyField> | null;
  appUrl?: string | null;
};

export const useIntegrationTools = (slug: string): UseIntegrationToolsResult => {
  const [status, setStatus] = useState<ToolsStatus>("loading");
  const [tools, setTools] = useState<ReadonlyArray<IntegrationTool>>([]);
  const [apiKeyFields, setApiKeyFields] = useState<ReadonlyArray<ApiKeyField>>([]);
  const [appUrl, setAppUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (slug === "") return undefined;
    const controller = new AbortController();
    setStatus("loading");
    setError(null);

    void fetch(`/api/public/integrations/${encodeURIComponent(slug)}`, {
      credentials: "include",
      signal: controller.signal,
    })
      .then(async (res: Response): Promise<void> => {
        if (!res.ok) {
          throw new Error(`integration detail fetch failed (${res.status})`);
        }
        const body: DetailResponse = (await res.json()) as DetailResponse;
        setTools(body.tools ?? []);
        setApiKeyFields(body.apiKeyFields ?? []);
        setAppUrl(body.appUrl ?? null);
        setStatus("ready");
      })
      .catch((err: unknown): void => {
        if (err instanceof DOMException && err.name === "AbortError") return;
        setError(err instanceof Error ? err.message : String(err));
        setStatus("error");
      });

    return () => controller.abort();
  }, [slug]);

  return { status, tools, apiKeyFields, appUrl, error };
};
